<template>
  <section
    :class="[
      'traceability-results-item',
      `traceability-results-item-${detailsLevel}`,
    ]"
    @click="handleClickResult"
  >
    <header class="traceability-results-item-header">
      <h2 class="traceability-results-item-title">
        <svg-loader
          v-if="detailsLevel === TraceabilityDetailsLevel.Country"
          class="traceability-results-item-title-icon"
          src="/static/images/icon-business.svg"
        />

        <div
          class="traceability-results-item-title-text"
          :title="result.destination"
        >
          {{ result.destination }}
        </div>
      </h2>

      <div class="traceability-results-item-total">
        <div
          v-if="detailsLevel === TraceabilityDetailsLevel.State"
          class="traceability-results-item-total-label"
        >
          {{ valueLabel }}
        </div>

        <div class="traceability-results-item-total-value">
          {{ result.total }}
        </div>
      </div>
    </header>

    <div
      v-if="detailsLevel === TraceabilityDetailsLevel.State"
      class="traceability-results-item-list"
    >
      <div
        v-for="event in result.status"
        :key="event.statusName"
        class="traceability-results-item-list-item"
      >
        <div class="traceability-results-item-list-item-content">
          <svg-loader
            class="traceability-results-item-list-item-content-icon"
            :src="getEventIconSource(event.statusName)"
            :style="{
              color: getEventColor(event.statusName)
            }"
          />

          <div class="traceability-results-item-list-item-content-label">
            {{ getEventLabel(event.statusName) }}
          </div>
        </div>

        <div class="traceability-results-item-list-item-content-value">
          {{ event.total }}
        </div>
      </div>
    </div>

    <footer
      v-if="detailsLevel === TraceabilityDetailsLevel.State"
      class="traceability-results-item-footer"
    >
      <md-button
        class="btn btn-primary traceability-results-item-footer-button"
        @click.stop="handleClickDetails"
      >
        <svg-loader
          src="/static/images/icon-search.svg"
          class="traceability-results-item-footer-button-icon"
        />

        {{ buttonText }}
      </md-button>
    </footer>
  </section>
</template>

<script>
import { TraceabilityDetailsLevel, TraceabilityStatus } from '@/enums'

import {
  traceabilityStatusLabels,
  traceabilityStatusAlternativeNamesMap,
  traceabilityExceptionalStatusLabels
} from '@/constants'

const statusColorVariation = {
  neutral: '#666666',
  positive: '#52CC9C',
  warning: '#F78E26',
  negative: '#F05253',
  info: '#8AD4DF',
}

const traceabilityLocalStatusColors = {
  [TraceabilityStatus.Triage]: statusColorVariation.neutral,
  [TraceabilityStatus.InExecution]: statusColorVariation.warning,
  [TraceabilityStatus.PendingCreated]: statusColorVariation.warning,
  [TraceabilityStatus.PendingAnswered]: statusColorVariation.positive,
  [TraceabilityStatus.Recalled]: statusColorVariation.warning,
  [TraceabilityStatus.InRelease]: statusColorVariation.warning,
  [TraceabilityStatus.ReportReleased]: statusColorVariation.positive,
  [TraceabilityStatus.NotReceived]: statusColorVariation.negative,
  [TraceabilityStatus.Canceled]: statusColorVariation.negative,
  [TraceabilityStatus.WaitingForWithdrawal]: statusColorVariation.warning,
  [TraceabilityStatus.InTransit]: statusColorVariation.positive,
  [TraceabilityStatus.InTransitBetweenNTOs]: statusColorVariation.info,
}

/** @note Created to handle exceptional data that are treated as statuses, but are not. */
const traceabilityExceptionalStatusColors = {
  behindSchedule: statusColorVariation.negative,
  aheadSchedule: statusColorVariation.info,
};

export default {
  name: 'TraceabilityResultsItem',
  props: {
    result: {
      type: Object,
      required: true
    },
    detailsLevel: {
      type: Number,
      default: TraceabilityDetailsLevel.Country
    },
    type: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      TraceabilityDetailsLevel,
    }
  },
  computed: {
    buttonText() {
      return 'Acessar NTO'
    },
    valueLabel() {
      return 'Total de '+ this.type;
    }
  },
  methods: {
    handleClickResult() {
      this.$emit('click-result', this.result)
    },
    handleClickDetails() {
      this.$emit('click-details', this.result)
    },
    getEventIconSource(event) {
      return `/static/images/icon-traceability-event-${this.getFixedStatusName(event)}.svg`
    },
    getEventLabel(event) {
      const eventName = this.getFixedStatusName(event)

      return traceabilityStatusLabels[eventName] ?? traceabilityExceptionalStatusLabels[eventName]
    },
    getEventColor(event) {
      const eventName = this.getFixedStatusName(event)

      return traceabilityLocalStatusColors[eventName] ?? traceabilityExceptionalStatusColors[eventName]
    },
    getFixedStatusName(event) {
      return traceabilityStatusAlternativeNamesMap[event] ?? event
    }
  }
}
</script>

<style lang="scss" scoped>
.traceability-results-item {
  font-family: 'Exo 2';
  color: #818181;

  &-header {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  &-title {
    font-weight: 700;
    font-size: 1.125rem;
    max-width: 100%;

    &-text {
      display: block;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      flex: 1;
      font-size: 1rem;
    }
  }

  &-total {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;

    &-label {
      text-transform: uppercase;
      font-size: 0.875rem;
      font-weight: 600;
    }

    &-value {
      color: #EC2072;
      font-size: 1.5rem;
      font-weight: 700;
    }
  }

  &-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.5rem;

    &-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      justify-content: space-between;

      &-content {
        display: flex;
        align-items: center;
        gap: 0.25rem;

        &-icon {
          width: 1.5rem;
          height: 1.5rem;
          min-width: auto;
          min-height: auto;
        }

        &-label {
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
        }
      }

      &-value {
        font-weight: 400;
        font-size: 1rem;
      }
    }
  }

  &-footer {
    &-button {
      width: 100%;

      .md-icon {
        fill: #FFFFFF !important;
      }
    }
  }

  &-0 {
    border: 1px solid #EC2072;
    border-radius: 0.25rem;
    padding: 0.5rem;
    cursor: pointer;
  }

  &-0 &-header {
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 0;
  }

  &-0 &-title {
    align-items: center;
    display: flex;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }
}
</style>
